// src/contexts/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the shape of the user data
export interface User { 
  id: number;
  username: string;
  email: string;
  alternateEmail?: string;
  mobile: string;
  alternateMobile?: string;
  userType: 'buyer' | 'seller';
  token?: string; // ✅ Added Token field
  sessionId?: number;
  
  profilePictureUrl?: string; 
  addressLine1?: string;      
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  
  // --- B2B Buyer Fields ---
  businessName?: string;
  businessCategory?: string;
  gstin?: string;
  preferredProducts?: string;
  annualTurnover?: string;
  
  // --- Seller Fields ---
  companyName?: string;
  companyWebsite?: string;
  pan?: string;
  ifscCode?: string;
  accountNumber?: string;
  bankName?: string;
  accountType?: string;
}

interface UserContextType {
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  updateUser: (updatedFields: Partial<User>) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    // Save user details
    localStorage.setItem('currentUser', JSON.stringify(userData)); 

    // ✅ Save JWT Token separately for API calls
    if (userData.token) {
      if (userData.userType === 'seller') {
        localStorage.setItem('sellerToken', userData.token);
      } else {
        localStorage.setItem('buyerToken', userData.token);
      }
    }
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    // Sirf tab chalega jab user login ho, seller ho, aur sessionId maujood ho
    if (user && user.userType === 'seller' && user.sessionId) {
      
      const sendHeartbeat = async () => {
        try {
          // Backend API call to update LastActiveTime
          await fetch(`/springboot/api/seller/activity/heartbeat/${user.sessionId}`, {
            method: 'POST',
            headers: {
               // Token bhejna zaruri ho sakta hai agar security lagayi hai
              'Authorization': `Bearer ${localStorage.getItem('sellerToken')}` 
            }
          });
          console.log("Seller activity updated.");
        } catch (error) {
          console.error("Heartbeat failed:", error);
        }
      };

      // Turant ek baar call karein
      sendHeartbeat();

      // Har 5 minute (300,000 ms) mein repeat karein
      intervalId = setInterval(sendHeartbeat, 5 * 60 * 1000);
    }

    // Cleanup: Jab user logout kare ya component band ho to timer rok dein
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user]);


const logout = async () => {
    // Agar seller hai to logout par final update bhej de
    if (user?.userType === 'seller' && user?.sessionId) {
        try {
            await fetch(`/springboot/api/seller/activity/logout/${user.sessionId}`, {
                method: 'POST' 
            });
        } catch(e) { console.error(e); }
    }

    setUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sellerToken');
    localStorage.removeItem('buyerToken');
  };
  
  const updateUser = (updatedFields: Partial<User>) => {
    setUser(prevUser => {
      if (!prevUser) return null;
      const newUser = { ...prevUser, ...updatedFields };
      localStorage.setItem('currentUser', JSON.stringify(newUser));
      return newUser;
    });
  };

  return (
    <UserContext.Provider value={{ user, login, logout, updateUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};